package diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme

import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Shapes
import androidx.compose.material3.Typography
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.unit.LayoutDirection
import diana.soleil.design.iraniancanadienbazzar.core.model.Language

/**
 * App design system entry point. Wrap the whole app once with [AppTheme]; read tokens everywhere via
 * `AppTheme.colors`, `AppTheme.typography`, `AppTheme.spacing`, `AppTheme.shapes`, `AppTheme.elevation`.
 *
 * Feature code must use these tokens — never raw hex/dp/sp/font values (see CLAUDE.md golden rules).
 */
object AppTheme {
    val colors: AppColors
        @Composable @ReadOnlyComposable get() = LocalAppColors.current
    val typography: AppTypography
        @Composable @ReadOnlyComposable get() = LocalAppTypography.current
    val spacing: AppSpacing
        @Composable @ReadOnlyComposable get() = LocalAppSpacing.current
    val shapes: AppShapes
        @Composable @ReadOnlyComposable get() = LocalAppShapes.current
    val elevation: AppElevation
        @Composable @ReadOnlyComposable get() = LocalAppElevation.current
    val sizes: AppSizes
        @Composable @ReadOnlyComposable get() = LocalAppSizes.current
    val gradients: AppGradients
        @Composable @ReadOnlyComposable get() = LocalAppGradients.current
    val language: Language
        @Composable @ReadOnlyComposable get() = LocalAppLanguage.current
    /**
     * Type scale for Farsi text shown inside a NON-Farsi session — a poem's original verse under its
     * English translation, a business's Farsi name on an English card. Always Vazirmatn, never
     * tracked; identical to [typography] when the session itself is Farsi.
     */
    val farsiTypography: AppTypography
        @Composable @ReadOnlyComposable get() = LocalAppFarsiTypography.current
}

val LocalAppColors = staticCompositionLocalOf { lightAppColors }
val LocalAppTypography = staticCompositionLocalOf { appTypography(FontFamily.SansSerif) }
val LocalAppFarsiTypography = staticCompositionLocalOf { appTypography(FontFamily.SansSerif, tracked = false) }
val LocalAppSpacing = staticCompositionLocalOf { appSpacing }
val LocalAppShapes = staticCompositionLocalOf { appShapes }
val LocalAppElevation = staticCompositionLocalOf { appElevation }
val LocalAppSizes = staticCompositionLocalOf { appSizes }
val LocalAppGradients = staticCompositionLocalOf { appGradients }

/** Active app/content language. Read via `AppTheme.language`; drives `LocalizedText` resolution + RTL. */
val LocalAppLanguage = staticCompositionLocalOf { Language.Default }

@Composable
fun AppTheme(
    language: Language = Language.Default,
    // Resolved by the composition root from `AppearanceController` (System / Light / Dark) — App()
    // maps System through `isSystemInDarkTheme()`. Defaults to false so DS previews and any host that
    // doesn't care keep the light Stitch look.
    //
    // Dark was enabled 2026-08-02 after auditing `darkAppColors` for WCAG AA: every foreground token
    // clears 4.5:1 against all four dark grounds, and `outlineVariant` was corrected (it had been
    // DARKER than surfaceContainerHigh, so card hairlines were invisible at 1.03:1).
    darkTheme: Boolean = false,
    content: @Composable () -> Unit,
) {
    val colors = if (darkTheme) darkAppColors else lightAppColors
    // Farsi renders with Vazirmatn (Public Sans has no Arabic glyphs); Latin languages use Public
    // Sans. Farsi also gets zero letter-spacing — tracking breaks joined Perso-Arabic letterforms.
    val fontFamily = if (language == Language.FA) vazirmatnFamily() else publicSansFamily()
    val typography = appTypography(fontFamily, tracked = language != Language.FA)
    // Farsi-inside-Latin sessions (see AppTheme.farsiTypography) reuse the Farsi scale as-is.
    val farsiTypography = if (language == Language.FA) typography else appTypography(vazirmatnFamily(), tracked = false)
    val layoutDirection = if (language.isRtl) LayoutDirection.Rtl else LayoutDirection.Ltr

    CompositionLocalProvider(
        LocalAppColors provides colors,
        LocalAppTypography provides typography,
        LocalAppFarsiTypography provides farsiTypography,
        LocalAppSpacing provides appSpacing,
        LocalAppShapes provides appShapes,
        LocalAppElevation provides appElevation,
        LocalAppSizes provides appSizes,
        LocalAppGradients provides appGradients,
        LocalAppLanguage provides language,
        LocalLayoutDirection provides layoutDirection,
    ) {
        MaterialTheme(
            colorScheme = colors.toMaterialColorScheme(),
            typography = typography.toMaterialTypography(),
            shapes = appShapes.toMaterialShapes(),
            content = content,
        )
    }
}

private fun AppColors.toMaterialColorScheme() =
    if (isDark) {
        darkColorScheme(
            primary = primary, onPrimary = onPrimary,
            primaryContainer = primaryContainer, onPrimaryContainer = onPrimaryContainer,
            secondary = secondary, onSecondary = onSecondary,
            secondaryContainer = secondaryContainer, onSecondaryContainer = onSecondaryContainer,
            tertiary = tertiary, onTertiary = onTertiary,
            background = background, onBackground = onBackground,
            surface = surface, onSurface = onSurface,
            onSurfaceVariant = onSurfaceVariant,
            outline = outline, outlineVariant = outlineVariant,
            error = error, onError = onError,
        )
    } else {
        lightColorScheme(
            primary = primary, onPrimary = onPrimary,
            primaryContainer = primaryContainer, onPrimaryContainer = onPrimaryContainer,
            secondary = secondary, onSecondary = onSecondary,
            secondaryContainer = secondaryContainer, onSecondaryContainer = onSecondaryContainer,
            tertiary = tertiary, onTertiary = onTertiary,
            background = background, onBackground = onBackground,
            surface = surface, onSurface = onSurface,
            onSurfaceVariant = onSurfaceVariant,
            outline = outline, outlineVariant = outlineVariant,
            error = error, onError = onError,
        )
    }

private fun AppTypography.toMaterialTypography() = Typography(
    displayLarge = displayLg,
    headlineLarge = headlineLg,
    headlineMedium = headlineMd,
    titleLarge = headlineMd,
    bodyLarge = bodyLg,
    bodyMedium = bodyMd,
    bodySmall = bodySm,
    labelLarge = labelMd,
    labelMedium = labelMd,
    labelSmall = labelSm,
)

private fun AppShapes.toMaterialShapes() = Shapes(
    extraSmall = sm,
    small = md,
    medium = tile,
    large = card,
    extraLarge = xl,
)
